import {
  BarChartOutlined,
  FolderOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined
} from "@ant-design/icons"
import {
  Avatar,
  Button,
  Card,
  Col,
  Flex,
  Row,
  Tag,
  Typography,
  theme
} from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { empty } from "../../../../constants/placeholders.ts"
import { SetupIncompleteBadge } from "../../components/SetupIncompleteBadge.tsx"
import {
  subcontractorPortalAccessLabels,
  subcontractorStatusColorMap
} from "../../constants/suctontractor-statuses.ts"
import { type SubcontractorListItem } from "../../types/types.ts"

const { Title, Text } = Typography

const getInitials = (name: string) => {
  const [first, second] = name.trim().split(/\s+/).filter(Boolean)
  return `${first?.[0] ?? ""}${second?.[0] ?? ""}`.toUpperCase() || "?"
}

const KpiCard = ({
  icon,
  label,
  value
}: {
  icon: React.ReactNode
  label: string
  value: number
}) => {
  const { token } = theme.useToken()

  return (
    <Card size={"small"} className={"h-full"}>
      <Flex vertical gap={8}>
        <Flex
          align={"center"}
          justify={"center"}
          aria-hidden
          className={"size-10 shrink-0 rounded-full"}
          style={{
            background: token.colorPrimaryBg,
            color: token.colorPrimary
          }}
        >
          {icon}
        </Flex>
        <Text type={"secondary"} className={"text-xs"}>
          {label}
        </Text>
        <Text className={"text-3xl font-semibold leading-none"}>{value}</Text>
      </Flex>
    </Card>
  )
}

interface Props {
  subcontractor: SubcontractorListItem
  businessName: string
  contactName?: string | null
  contactEmail?: string | null
  contactPhone?: string | null
  trades: string[]
  isEditable: boolean
  onEdit: () => void
}

/**
 * The profile card and the two project counters as one balanced row: the
 * identity takes roughly 60% of a desktop row and the counters share the rest,
 * instead of a stretched header above two full-width boxes.
 */
export const SubcontractorProfileHeader = ({
  subcontractor,
  businessName,
  contactName,
  contactEmail,
  contactPhone,
  trades,
  isEditable,
  onEdit
}: Props) => {
  const { t } = useTranslation()
  const normalizedStatus =
    subcontractor.status?.toLowerCase() as keyof typeof subcontractorStatusColorMap

  return (
    <Row gutter={[16, 16]} align={"stretch"}>
      <Col xs={24} lg={14}>
        <Card size={"small"} className={"h-full"}>
          <Flex gap={16} align={"flex-start"} wrap>
            <Avatar size={64} className={"shrink-0"}>
              {getInitials(businessName)}
            </Avatar>
            <Flex vertical gap={8} className={"min-w-0 flex-1"}>
              <Flex gap={12} align={"center"} wrap>
                <Title level={4} className={"!mb-0 break-words"}>
                  {businessName}
                </Title>
                {isEditable && (
                  <Button size={"small"} onClick={onEdit}>
                    {t("subcontractorDetail.edit")}
                  </Button>
                )}
              </Flex>
              <Flex gap={8} wrap align={"center"}>
                {trades.map((trade) => (
                  <Tag key={trade}>{trade}</Tag>
                ))}
              </Flex>
              <Flex gap={16} wrap align={"center"}>
                {contactName && (
                  <Flex gap={4} align={"center"}>
                    <UserOutlined aria-hidden className={"text-gray-400"} />
                    <Text type={"secondary"}>{contactName}</Text>
                  </Flex>
                )}
                <Flex gap={4} align={"center"} className={"min-w-0"}>
                  <MailOutlined aria-hidden className={"text-gray-400"} />
                  <Text type={"secondary"} className={"break-all"}>
                    {contactEmail || empty}
                  </Text>
                </Flex>
                <Flex gap={4} align={"center"}>
                  <PhoneOutlined aria-hidden className={"text-gray-400"} />
                  <Text type={"secondary"}>{contactPhone || empty}</Text>
                </Flex>
              </Flex>
              <Flex gap={8} wrap align={"center"}>
                <Text type={"secondary"}>
                  {t("subcontractorDetail.portalAccess")}:
                </Text>
                <Tag
                  data-testid={"portal-access-tag"}
                  color={
                    subcontractorStatusColorMap[normalizedStatus] ?? "default"
                  }
                >
                  {subcontractorPortalAccessLabels[normalizedStatus] ??
                    subcontractor.status ??
                    empty}
                </Tag>
                {/* Being invited and being complete are different questions,
                    so the readiness badge sits beside portal access rather
                    than inside it. Renders nothing for a complete profile. */}
                <SetupIncompleteBadge readiness={subcontractor.readiness} />
              </Flex>
            </Flex>
          </Flex>
        </Card>
      </Col>
      <Col xs={12} lg={5}>
        <KpiCard
          icon={<FolderOutlined />}
          label={t("subcontractorDetail.totalActiveProjects")}
          value={subcontractor.active_assignments_count ?? 0}
        />
      </Col>
      <Col xs={12} lg={5}>
        <KpiCard
          icon={<BarChartOutlined />}
          label={t("subcontractorDetail.totalProjects")}
          value={subcontractor.assignments_count ?? 0}
        />
      </Col>
    </Row>
  )
}
